import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Users,
  Building2,
  ShoppingBag,
  TrendingUp,
  ChefHat,
  Store,
  Plus,
  Leaf,
  Drumstick
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Treemap, Legend 
} from 'recharts';

const COLORS = ['hsl(24, 95%, 53%)', 'hsl(142, 76%, 36%)', 'hsl(48, 96%, 53%)', 'hsl(20, 14%, 30%)', 'hsl(200, 70%, 50%)'];
const VEG_COLOR = '#22c55e';
const NON_VEG_COLOR = '#ef4444';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [entityAnalytics, setEntityAnalytics] = useState([]);
  const [sunburstData, setSunburstData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [overviewRes, entitiesRes, sunburstRes] = await Promise.all([
        analyticsAPI.getOverview(),
        analyticsAPI.getEntities(),
        analyticsAPI.getSunburst()
      ]);
      setAnalytics(overviewRes.data);
      setEntityAnalytics(entitiesRes.data);
      setSunburstData(sunburstRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const stats = analytics ? [
    { label: 'Total Buyers', value: analytics.total_buyers, icon: Users, color: 'text-blue-500' },
    { label: 'Home Cooks', value: analytics.total_sellers, icon: ChefHat, color: 'text-primary' },
    { label: 'Vendors', value: analytics.total_dealers, icon: Store, color: 'text-purple-500' },
    { label: 'Communities', value: analytics.total_entities, icon: Building2, color: 'text-secondary' },
    { label: 'Total Orders', value: analytics.total_orders, icon: ShoppingBag, color: 'text-orange-500' },
    { label: 'Revenue', value: `₹${analytics.total_revenue}`, icon: TrendingUp, color: 'text-emerald-500' },
  ] : [];

  const userPieData = analytics ? [
    { name: 'Buyers', value: analytics.total_buyers },
    { name: 'Sellers', value: analytics.total_sellers },
    { name: 'Dealers', value: analytics.total_dealers },
  ] : [];

  const vegNonVegData = analytics ? [
    { name: 'Vegetarian', value: analytics.veg_orders || 0, color: VEG_COLOR },
    { name: 'Non-Vegetarian', value: analytics.non_veg_orders || 0, color: NON_VEG_COLOR },
  ] : [];

  // Transform sunburst data for Treemap
  const getTreemapData = () => {
    if (!sunburstData || !sunburstData.children) return [];
    
    const data = [];
    sunburstData.children.forEach(community => {
      community.children?.forEach(category => {
        category.children?.forEach(person => {
          data.push({
            name: person.name,
            size: person.value || 1,
            category: category.name,
            community: community.name
          });
        });
      });
    });
    return data;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card p-3 rounded-lg shadow-lg border border-border">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-muted-foreground">{data.category} • {data.community}</p>
          <p className="text-sm text-primary">₹{data.size}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/admin/entities/new')}
              className="rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Community
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-8 w-8 mb-2" />
                  <Skeleton className="h-6 w-16 mb-1" />
                  <Skeleton className="h-4 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((stat, idx) => (
              <Card key={idx} className="stats-card">
                <CardContent className="p-4">
                  <stat.icon className={`w-8 h-8 ${stat.color} mb-2`} />
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>User Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={userPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {userPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Veg vs Non-Veg Orders */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Leaf className="w-5 h-5 text-green-500" />
                Veg vs Non-Veg Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : vegNonVegData.every(d => d.value === 0) ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No order data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={vegNonVegData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {vegNonVegData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      verticalAlign="bottom" 
                      formatter={(value, entry) => (
                        <span className="flex items-center gap-1">
                          {value === 'Vegetarian' ? <Leaf className="w-4 h-4" /> : <Drumstick className="w-4 h-4" />}
                          {value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Community Performance */}
          <Card>
            <CardHeader>
              <CardTitle>Community Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : entityAnalytics.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No community data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={entityAnalytics.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="entity_name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="orders_count" fill="hsl(24, 95%, 53%)" name="Orders" />
                    <Bar dataKey="sellers_count" fill="hsl(142, 76%, 36%)" name="Sellers" />
                    <Bar dataKey="buyers_count" fill="hsl(200, 70%, 50%)" name="Buyers" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Sunburst-style Treemap */}
          <Card>
            <CardHeader>
              <CardTitle>Community → Users → Revenue Map</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : getTreemapData().length === 0 ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No hierarchical data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <Treemap
                    data={getTreemapData()}
                    dataKey="size"
                    aspectRatio={4 / 3}
                    stroke="#fff"
                    fill="hsl(24, 95%, 53%)"
                    content={({ x, y, width, height, name, category }) => {
                      if (width < 30 || height < 20) return null;
                      return (
                        <g>
                          <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            style={{
                              fill: category === 'Sellers' ? 'hsl(142, 76%, 36%)' : 'hsl(200, 70%, 50%)',
                              stroke: '#fff',
                              strokeWidth: 2,
                            }}
                          />
                          {width > 50 && height > 30 && (
                            <text
                              x={x + width / 2}
                              y={y + height / 2}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize={10}
                            >
                              {name}
                            </text>
                          )}
                        </g>
                      );
                    }}
                  >
                    <Tooltip content={<CustomTooltip />} />
                  </Treemap>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="cursor-pointer card-hover"
            onClick={() => navigate('/admin/entities')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold">Manage Communities</p>
                <p className="text-sm text-muted-foreground">Add, edit, or remove</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer card-hover"
            onClick={() => navigate('/admin/users')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Manage Users</p>
                <p className="text-sm text-muted-foreground">View all users</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer card-hover"
            onClick={() => navigate('/admin/orders')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="font-semibold">View Orders</p>
                <p className="text-sm text-muted-foreground">All platform orders</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
